import type { SyntheticEvent } from "react";
import { Building2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationMembership } from "@forgepoint/shared";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useOrganizationMembershipsStore } from "@/store/organizationMembershipsStore";

/**
 * Organizations section of the sidebar footer menu. Renders nothing for users
 * with a single membership, so their menu is unchanged, nor until the caller is
 * rolled onto memberships.
 */
export function OrgSwitcherMenuItems() {
  const isMultiOrgEnabled = useFeatureFlag("multi-org-membership");
  const membershipMap = useOrganizationMembershipsStore(
    (state) => state.membershipMap,
  );
  const switchStates = useOrganizationMembershipsStore(
    (state) => state.switchStates,
  );
  const switchOrganization = useOrganizationMembershipsStore(
    (state) => state.switchOrganization,
  );

  const memberships = [...membershipMap.values()];
  const useModal = memberships.length > 3;
  const isSwitching = Object.values(switchStates).some(
    (state) => state.isLoadingSwitch,
  );

  if (!isMultiOrgEnabled || memberships.length < 2) {
    return null;
  }

  /**
   * Switches into the picked organization, keeping the menu open while it runs.
   * @param membership - The membership row that was picked
   */
  const handleSelect = async (membership: OrganizationMembership) => {
    if (membership.isActive || isSwitching) {
      return;
    }
    await switchOrganization(membership.organizationId);
    const switchError =
      useOrganizationMembershipsStore.getState().switchStates[
        membership.organizationId
      ]?.switchError;
    if (switchError) {
      toast.error(switchError);
    }
  };

  const items = memberships.map((membership) => {
    const isSwitchingToThisOrg =
      switchStates[membership.organizationId]?.isLoadingSwitch === true;
    const content = (
      <>
        <span
          className={cn(
            "relative flex size-6 flex-none items-center justify-center overflow-hidden rounded-md text-[11px] font-semibold",
            membership.isActive
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground",
          )}
          aria-hidden="true"
        >
          {getOrganizationInitials(membership.organizationName)}
          {membership.organizationLogoUrl && (
            <img
              src={membership.organizationLogoUrl}
              alt=""
              className="absolute inset-0 size-full bg-background object-contain"
              onError={hideBrokenLogo}
            />
          )}
        </span>
        <span className="flex-1 truncate" title={membership.organizationName}>
          {membership.organizationName}
        </span>
        {isSwitchingToThisOrg && (
          <span className="ml-auto flex flex-none text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
          </span>
        )}
        {membership.isActive && (
          <span className="ml-auto flex flex-none">
            <Check className="size-3.5" />
          </span>
        )}
      </>
    );
    return useModal ? (
      <Button
        key={membership.organizationId}
        variant="ghost"
        className="w-full justify-start gap-2 text-left"
        disabled={isSwitching && !isSwitchingToThisOrg}
        onClick={() => handleSelect(membership)}
      >
        {content}
      </Button>
    ) : (
      <DropdownMenuItem
        key={membership.organizationId}
        disabled={isSwitching && !isSwitchingToThisOrg}
        onSelect={(event) => {
          event.preventDefault();
          handleSelect(membership);
        }}
      >
        {content}
      </DropdownMenuItem>
    );
  });

  return (
    <>
      {useModal ? (
        <Dialog>
          <DialogTrigger asChild>
            <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
              <Building2 className="mr-2 size-4" />
              Switch organization
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogTitle>Switch organization</DialogTitle>
            <div className="max-h-[60vh] overflow-y-auto p-1">{items}</div>
          </DialogContent>
        </Dialog>
      ) : (
        <>
          <DropdownMenuLabel className="px-2 pb-0.5 pt-1.5 text-[11px] font-medium text-muted-foreground">
            Organizations
          </DropdownMenuLabel>
          {items}
        </>
      )}
      <DropdownMenuSeparator />
    </>
  );
}

/**
 * Hides a logo that fails to load, uncovering the initials underneath it.
 * @param event - The image error event
 */
function hideBrokenLogo(event: SyntheticEvent<HTMLImageElement>): void {
  event.currentTarget.style.display = "none";
}

/**
 * Builds a tile label from an organization name: the first letter of its first
 * two words, or its first two letters when the name is a single word.
 * @param organizationName - The organization's display name
 * @returns One or two uppercase initials
 */
function getOrganizationInitials(organizationName: string): string {
  const words = organizationName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "?";
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}
